import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styles from './index.module.scss'
import { Button, Modal } from 'antd'
import { Game } from 'types/games'
import { createGame, deleteGame, editGame, getGameById, getGames, getGamesInLeague } from 'services/games'
import { GamesTable } from '../../../components/games-table'
import { GameForm } from '../../../components/game-form'
import { Link, useParams } from 'react-router-dom'
import { League } from 'types/leagues'
import { LEAGUE_FORMAT_STAGES_ID } from 'types/league-formats'
import { getLeagueById } from 'services/leagues'
import { RosterCreator, SelectedRosterProps } from '../../../components/roster-creator'
import GameLinksForm from '../../../components/game-links-form'
import { gameTeamsLabel, modalTitle as formatModalTitle } from 'utils/modal-title'
import dayjs from 'dayjs'

type RosterModalContext = SelectedRosterProps & {
    teamName: string
}

const getTeamFromGame = (game: Game | undefined, teamId: number) => {
    if (!game) {
        return undefined
    }

    if (game.teamHome.team.id === teamId) {
        return game.teamHome.team
    }

    if (game.teamAway.team.id === teamId) {
        return game.teamAway.team
    }

    return undefined
}

export const Games = memo(() => {
    const { league: leagueId } = useParams()
    const [games, setGames] = useState<Game[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [game, setGame] = useState<Game>({} as Game)
    const [roster, setRoster] = useState<RosterModalContext>({} as RosterModalContext)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const [openRoster, setOpenRoster] = useState<boolean>(false)
    const [openGameLinks, setOpenGameLinks] = useState<boolean>(false)

    const [league, setLeague] = useState<League>()

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await (leagueId ? getGamesInLeague(Number(leagueId)) : getGames())
        }

        getAll()
            .then(data => {
                setGames(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [leagueId])

    const getLeagueInfo = useCallback(() => {
        getLeagueById(Number(leagueId)).then(setLeague)
    }, [leagueId])

    useEffect(() => {
        getAllData()
        getLeagueInfo()
    }, [getAllData, getLeagueInfo])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setGame({} as Game)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getGameById(id)
                .then(item => {
                    setGame(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const toggleGameLinksModal = useCallback(() => {
        setOpenGameLinks(!openGameLinks)
    }, [openGameLinks])

    const handleMedia = useCallback(
        (id: number) => {
            setLoading(true)
            getGameById(id)
                .then(item => {
                    setGame(item)
                    toggleGameLinksModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleGameLinksModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteGame(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        (submitValues: Game) => {
            setLoading(true)

            const refetchDataAndCloseModal = () => {
                getAllData()
                toggleModal()
            }

            submitValues.leagueHomeId = Number(leagueId)
            submitValues.leagueAwayId = Number(leagueId)

            if (game.id) {
                editGame({
                    ...submitValues,
                    id: game.id,
                })
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            } else {
                createGame(submitValues)
                    .then(refetchDataAndCloseModal)
                    .finally(() => {
                        setLoading(false)
                    })
            }
        },
        [getAllData, game, toggleModal, leagueId]
    )

    const toggleRosterModal = useCallback(() => {
        setOpenRoster(!openRoster)
    }, [openRoster])

    const handleShowRoster = useCallback(
        (teamId: number, gameId: number) => {
            const selectedGame = games.find(it => it.id === gameId)
            const team = getTeamFromGame(selectedGame, teamId)

            setRoster({
                teamId,
                gameId,
                leagueId: Number(leagueId),
                teamName: team?.name ?? `Team #${teamId}`,
            })
            toggleRosterModal()
        },
        [games, toggleRosterModal, leagueId]
    )

    const editMode = !!game.id
    const gameFormModalTitle = useMemo(() => {
        if (editMode && game.teamHome?.team && game.teamAway?.team) {
            return formatModalTitle(
                `Edit game #${game.id}`,
                gameTeamsLabel(game.teamHome.team.name, game.teamAway.team.name)
            )
        }

        return formatModalTitle('Add new game', league?.name)
    }, [editMode, game, league?.name])

    const rosterModalTitle = useMemo(
        () => formatModalTitle('Roster', `${roster.teamName} (game #${roster.gameId})`),
        [roster.gameId, roster.teamName]
    )

    const gameLinksModalTitle = useMemo(() => {
        if (!game.teamHome?.team || !game.teamAway?.team) {
            return 'Game links'
        }

        const teams = gameTeamsLabel(game.teamHome.team.name, game.teamAway.team.name)
        const dateLabel = game.gameTime ? dayjs(game.gameTime * 1000).format('DD.MM.YYYY') : null

        return formatModalTitle('Game links', dateLabel ? `${teams} (${dateLabel})` : teams)
    }, [game])

    const somethingLoading = loading || dataIsFetching

    return (
        <div>
            Games. League: {league?.name}
            <div className={styles.actions}>
                {league?.leagueFormatId === LEAGUE_FORMAT_STAGES_ID ? (
                    <Link to={`/leagues/${leagueId}/bracket`}>Bracket</Link>
                ) : null}
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <GamesTable
                data={games}
                onEdit={handleEdit}
                onMedia={handleMedia}
                onDelete={handleDelete}
                loading={somethingLoading}
                showRoster={handleShowRoster}
            />
            <Modal
                title={gameFormModalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <GameForm
                    game={game}
                    onSubmit={handleFormSubmit}
                    loading={somethingLoading}
                    leagueId={Number(leagueId)}
                />
            </Modal>
            <Modal
                title={rosterModalTitle}
                open={openRoster}
                destroyOnClose
                footer={null}
                onCancel={toggleRosterModal}
                maskClosable={false}
                width={1000}
            >
                <RosterCreator {...roster} />
            </Modal>
            <Modal
                title={gameLinksModalTitle}
                open={openGameLinks}
                destroyOnClose
                footer={null}
                onCancel={toggleGameLinksModal}
                maskClosable={false}
                width={1000}
            >
                <GameLinksForm game={game} onSubmit={toggleGameLinksModal} />
            </Modal>
        </div>
    )
})
